import { Select } from '@kobalte/core/select'
import { Show, type JSX } from 'solid-js'
import Css from './filters.module.scss'

export type ChoiceOption = {
    label: string
    value?: string
    current: boolean
    onSelect: () => Promise<void>
}

/**
 * O filtro de escolha unica.
 *
 * Nao ha opcao "Todos" na lista: o vazio e o `placeholder`, e quem limpa e o X ao
 * lado do valor. E como o painel antigo faz, e uma opcao a mais na lista mudaria a
 * contagem do que o leitor ve escolhido.
 */
export function ChoiceFilter(props: {
    label: string
    summary: string
    placeholder: string
    options: ChoiceOption[]
    hasValue: boolean
    disabled?: boolean
    onClear: () => void

    /**
     * Falso quando o vazio nao e uma escolha valida. O segmento de uma empresa e assim:
     * o servidor le nulo como "nao altera", e o X prometeria uma limpeza que nao acontece.
     */
    clearable?: boolean

    /** Sobre a faixa ESCURA de filtros - ver o `onDark` do `TextFilter`. */
    onDark?: boolean
}): JSX.Element {
    return (
        <div class={Css.field} classList={{ [Css.onDark]: props.onDark }}>
            <span class={Css.label}>{props.label}</span>

            <div class={Css.choice} classList={{ [Css.disabled]: props.disabled }}>
                {/*
                    CONTROLADO pelo scope (`value`), e nao pela memoria do proprio
                    componente.
                    
                    Sem o `value`, o `Select` guarda a escolha por dentro e a redispara
                    sempre que a lista de opcoes chega nova - e ela chega nova a cada
                    `showRequest`, que refaz os filtros. Isso produziu dois defeitos: um
                    laco infinito ao escolher (o redisparo voltava ao `onSelect`, que
                    navegava, que refazia a lista...) e, depois de barrado o laco, o "x"
                    que nao limpava: o componente reaplicava na hora o valor que a tela
                    acabara de tirar.
                    
                    Com `value` vindo de fora, quem manda e o estado da tela: limpar
                    limpa, e `onChange` so fala quando a pessoa escolhe.
                */}
                <Select<ChoiceOption>
                    class={Css.selection}
                    options={props.options}
                    value={props.options.find((option) => option.current) ?? null}
                    disabled={props.disabled}
                    optionValue={(option) => option.value ?? option.label}
                    optionTextValue={(option) => option.label}
                    /*
                        `!option.current`: o componente nao reage ao redisparo do que JA
                        esta escolhido.
                        
                        Mesmo controlado, o `Select` avisa de novo quando a lista de
                        opcoes chega nova - e ela chega nova a cada `showRequest`, porque
                        o presenter refaz os filtros. Sem esta condicao, o aviso volta ao
                        `onSelect`, que navega, que refaz a lista, que avisa outra vez.
                        
                        Medido em 20/09/2026: 8.608 chamadas em dois segundos na lista de
                        Colaboradores (ate `ERR_INSUFFICIENT_RESOURCES` e pilha estourada)
                        e 8.718 em Empresas - onde o laco nao travava a tela, mas desfazia
                        o "x": limpar e reaplicar o valor no mesmo instante.
                        
                        Fica AQUI, e nao em cada presenter, porque o defeito e do
                        componente: sao seis telas usando este filtro.
                    */
                    onChange={(option) => option && !option.current && void option.onSelect()}
                    placeholder={props.placeholder}
                    itemComponent={(itemProps) => (
                        <Select.Item item={itemProps.item} class={Css.option}>
                            <Select.ItemLabel class={Css.optionText}>{itemProps.item.rawValue.label}</Select.ItemLabel>
                        </Select.Item>
                    )}
                >
                    <Select.Trigger class={Css.trigger}>
                        <span class={Css.summary} classList={{ [Css.empty]: !props.hasValue }}>
                            {props.hasValue ? props.summary : props.placeholder}
                        </span>
                        <Select.Icon>
                            <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                class={Css.arrow}
                            >
                                <path d="m6 9 6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </Select.Icon>
                    </Select.Trigger>

                    <Select.Portal>
                        <Select.Content class={Css.list}>
                            <Select.Listbox />
                        </Select.Content>
                    </Select.Portal>
                </Select>

                {/* O X fica FORA do gatilho: dentro dele, limpar abriria a lista. */}
                <Show when={props.hasValue && !props.disabled && props.clearable !== false}>
                    <button
                        type="button"
                        aria-label={`Limpar ${props.label}`}
                        onClick={props.onClear}
                        class={Css.clear}
                    >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M18 6 6 18M6 6l12 12" stroke-linecap="round" />
                        </svg>
                    </button>
                </Show>
            </div>
        </div>
    )
}
